import { useRouteLoaderData } from "@remix-run/react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS } from "~/components/booking/flow/confirmButton";
import { Button } from "~/components/ui/Button";
import { keepAnimationOnMainThread } from "~/lib/motion";
import {
  cn,
  getCmsServiceCategoryFromList,
  getCmsServiceFromList,
} from "~/lib/utils";

const TimeSelect = ({
  loading,
  onConfirm,
  onCancel,
  selectedService,
  guestsCount,
  initialSelectedTime,
  selectedAvailability,
}: {
  loading: boolean;
  onCancel: () => void;
  onConfirm: (selectedTime: any) => void;
  selectedService: any;
  guestsCount?: { male: number; female: number };
  initialSelectedTime?: any;
  selectedAvailability?: any;
}) => {
  const { t, i18n } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  const [selectedTime, setSelectedTime] = useState<any>(initialSelectedTime);
  const totalGuests = guestsCount ? guestsCount.male + guestsCount.female : 0;

  useEffect(() => {
    if (initialSelectedTime) {
      setSelectedTime(initialSelectedTime);
    }
  }, [initialSelectedTime]);

  const normalizeTime = (time: string): string => {
    if (!time) return "";
    if (time.includes("T")) {
      const timePart = time.split("T")[1];
      return timePart ? timePart.split(".")[0] : time;
    }
    return time.split(".")[0];
  };

  const timesMatch = (time1: string, time2: string): boolean => {
    return normalizeTime(time1) === normalizeTime(time2);
  };

  const isTimeSelected = (time: any): boolean => {
    if (!selectedTime || !time) return false;
    return (
      timesMatch(selectedTime.StartTime, time.StartTime) &&
      timesMatch(selectedTime.EndTime, time.EndTime)
    );
  };

  const cmsServiceCategory = selectedAvailability?.category
    ? getCmsServiceCategoryFromList(
        rootData?.normalServiceCategories || [],
        selectedAvailability.category
      )
    : null;

  const cmsService = selectedService?.Code
    ? getCmsServiceFromList(rootData?.servicesData || [], selectedService.Code)
    : null;

  const codeObj = cmsService?.acf?.codes?.find(
    (c: any) => c.code === selectedService.Code
  );

  // Use image from code if available, otherwise fallback to service image, then category image
  const displayImage =
    codeObj?.image?.id ||
    cmsService?.acf?.image?.id ||
    cmsServiceCategory?.acf?.image?.id;
  const displayTitle = cmsService
    ? codeObj?.short_description ||
      cmsService.acf?.title ||
      cmsServiceCategory?.name
    : cmsServiceCategory?.name;
  const displayDescription = cmsService?.acf?.codes?.find(
    (c: any) => c.code === selectedService.Code
  )?.long_description;

  // `HideEndTime` è una proprietà a livello di SERVIZIO nell'API (cfr.
  // PackageStep, che la legge da `service.HideEndTime`), non sullo slot di
  // availability. Va quindi letta dal servizio selezionato; teniamo il
  // fallback sullo slot per retro-compatibilità nel caso un singolo slot la
  // valorizzi.
  const hideEndTime = !!(
    selectedService?.HideEndTime ||
    selectedService?.Availability?.some((time: any) => time.HideEndTime)
  );

  return (
    <div
      id="step-time-select"
      className="w-full max-w-3xl mx-auto flex flex-col px-8 py-8"
    >
      {displayImage && displayTitle && (
        <div
          className={cn(
            "mb-6 rounded-2xl overflow-hidden bg-white shadow-md mx-auto w-full max-w-md"
          )}
        >
          <div className="w-full h-60 relative">
            <motion.img
              onUpdate={keepAnimationOnMainThread}
              alt=""
              src={`/cms-asset/${displayImage}`}
              className="w-full h-full object-cover object-center z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.3,
                delay: 0.5,
                ease: "easeInOut",
              }}
            />
          </div>
          <div className="p-6">
            <h4 className="text-secondary font-bold text-xl leading-none">
              {displayTitle}
            </h4>
            {displayDescription && (
              <div
                dangerouslySetInnerHTML={{
                  __html: displayDescription,
                }}
                className="text-md text-gray-600 mt-2"
              />
            )}
          </div>
        </div>
      )}
      <h3 className="font-bold text-xl text-center">{t("Orario")}</h3>
      <p className="text-center mt-2">
        {t("Seleziona l'ora della prenotazione")}
      </p>
      <div
        className={cn(
          "w-full grid grid-cols-2 md:grid-cols-3  items-center mt-4 gap-4",
          {
            "xl:grid-cols-7": hideEndTime,
            "xl:grid-cols-4": !hideEndTime,
          }
        )}
      >
        {selectedService?.Availability?.map((time: any, index: number) => {
          const entranceHideEnd =
            selectedService?.HideEndTime || time.HideEndTime || false;
          const quantity = time.Quantity?.Total || 0;

          let isAvailable = false;

          if (selectedService?.GenderDistinction && guestsCount) {
            const manAvailable = time.Quantity?.Man || 0;
            const womanAvailable = time.Quantity?.Woman || 0;
            isAvailable =
              manAvailable >= guestsCount.male &&
              womanAvailable >= guestsCount.female &&
              quantity > 0;
          } else {
            isAvailable = quantity > 0;
          }

          return (
            <div
              key={index}
              className={cn(
                "w-full rounded-2xl overflow-hidden bg-white shadow-md cursor-pointer flex flex-row items-center justify-between px-4 py-2",
                {
                  "ring-2 ring-secondary": isTimeSelected(time),
                  "opacity-50 cursor-not-allowed": !isAvailable,
                }
              )}
              onClick={() => {
                // Single-choice step: picking a slot confirms it right away.
                // `loading` guards against a second click while the first
                // add-to-cart is still running.
                if (isAvailable && !loading) {
                  setSelectedTime(time);
                  onConfirm(time);
                }
              }}
            >
              <div className="flex flex-col w-full relative items-center">
                {!isAvailable && (
                  <span className="text-xs text-red-500 block">
                    {t("ESAURITO")}
                  </span>
                )}
                <div className="flex flex-row items-center justify-center">
                  <span className="leading-[1.2]">
                    {time.StartTime.slice(0, 5)}
                    {entranceHideEnd ? "" : ` - ${time.EndTime.slice(0, 5)}`}
                  </span>
                </div>
                {(() => {
                  const price = time.EffectivePrice?.[0]?.Price || time.Price;
                  return price && price > 0 ? (
                    <span className="text-sm text-secondary font-semibold">
                      €
                      {price.toLocaleString(i18n.language, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
          );
        })}
      </div>
      <div className="w-full flex flex-row items-center justify-center space-x-2 mt-4">
        <Button
          variant={"muted"}
          onClick={() => {
            onCancel();
          }}
        >
          <span>{t("Indietro")}</span>
        </Button>
        {SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS && (
          <Button
            disabled={loading || !selectedTime}
            variant={"secondary"}
            className={cn(" self-start relative", {
              "animate-pulse": loading,
            })}
            onClick={() => {
              onConfirm(selectedTime);
            }}
          >
            <span
              className={cn({
                "opacity-0": loading,
              })}
            >
              {t("Conferma")}
            </span>
            {loading && (
              <div className="absolute w-full h-full flex items-center justify-center">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-t-transparent"></div>
              </div>
            )}
          </Button>
        )}
        {/* With the button hidden, this is the only sign that the picked
            slot is being added to the cart. */}
        {!SHOW_CONFIRM_ON_AUTO_ADVANCE_STEPS && loading && (
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
        )}
      </div>
    </div>
  );
};

export default TimeSelect;
